import { Box3, Matrix4, Mesh } from 'three';
import type { BufferAttribute, BufferGeometry, InterleavedBufferAttribute, Object3D, Ray } from 'three';
import { acceleratedRaycast } from 'three-mesh-bvh';
import { AabbRayIndex } from '../spatial';
import { SceneMeshRaycastPolicy } from './SceneMeshRaycastPolicy';

/**
 * 共享物理成员监听，为普通静态顶点 Mesh 提供世界 AABB 粗筛。
 *
 * 只删除不可能命中的候选，精确求交仍由原对象执行；不修改 raycast、业务显隐或原生包围缓存。
 * 查询只校验当前候选的 matrixWorld 和位置版本；离开候选的对象再次参与时即时补校验。
 * 仅变化对象更新边界，兼容位姿与历史恢复入口，不将历史参与者全量检查当作每次查询前置条件。
 */
export class SceneMeshRaycastIndex {
  /** 空间树只拥有自有边界。 */
  private readonly index = new AabbRayIndex();
  /** 普通 Mesh 的成员记录。 */
  private readonly entryByObject = new Map<Object3D, SceneMeshRaycastEntry>();
  /** 物理成员仅用于确认归属；尚未参与候选的高亮/辅助 Mesh 不加入空间树。 */
  private readonly members = new Set<Mesh>();
  /** 当前候选到记录的复用缓冲，查询后清空引用，避免同一次输入重复 Map 查询。 */
  private readonly candidateEntries: Array<SceneMeshRaycastEntry | undefined> = [];
  /** 当前结构记录，删除时立即解除对象引用。 */
  private readonly entries: SceneMeshRaycastEntry[] = [];
  /** 几何不再使用时可由 GC 回收，不延长共享几何生命周期。 */
  private geometryBounds = new WeakMap<BufferGeometry, SceneMeshGeometryBounds>();
  /** 整数查询输出缓冲。 */
  private readonly hits: number[] = [];
  /** 多次物理增删合并为下一次结构构建。 */
  private structureDirty = false;
  /** 用查询代次记录保留集合，避免每次创建 Set。 */
  private query = 0;
  /** 累计局部盒计算次数。 */
  private geometryBoundsBuilds = 0;
  /** 几何边界的唯一修订分配器，独立于可观察的统计计数。 */
  private nextGeometryRevision = 0;
  /** 累计世界盒计算次数。 */
  private refreshedBounds = 0;
  /** 最近输入数量。 */
  private lastInput = 0;
  /** 最近输出数量。 */
  private lastOutput = 0;
  /** 只解除本消费者，不销毁共享源。 */
  private unsubscribe: (() => void) | null;

  /** @param membership Viewer 或独立宿主提供的物理成员源。 */
  public constructor(membership: SceneObjectMembershipSource) {
    this.unsubscribe = membership.subscribe({ onObjectAdded: this.onObjectAdded, onObjectRemoved: this.onObjectRemoved });
  }

  /**
   * 按原顺序输出候选，保留重复项与同距离 tie-break。
   * @param candidates 既有选择策略已确定的候选。
   * @param ray 原始 Raycaster 的世界射线。
   * @param target 调用方复用的输出数组；输入为调用方自有可变数组时可原位压缩。
   */
  public filterCandidates<T extends Object3D>(candidates: readonly T[], ray: Ray, target: T[]): void {
    this.lastInput = candidates.length;
    if (!this.unsubscribe || candidates.length === 0) { target.length = 0; this.lastOutput = 0; return; }
    // 非法射线保守透传，不猜测自定义精确求交的行为。
    if (candidates.length < SceneMeshRaycastPolicy.minimumCandidates
      || this.members.size < SceneMeshRaycastPolicy.minimumCandidates
      || !Number.isFinite(ray.origin.x + ray.origin.y + ray.origin.z + ray.direction.x + ray.direction.y + ray.direction.z)
      || ray.direction.lengthSq() === 0) {
      if (candidates !== target) {
        for (let id = 0; id < candidates.length; id++) target[id] = candidates[id];
        target.length = candidates.length;
      }
      this.lastOutput = target.length;
      return;
    }
    try {
      this.query++;
      let orderedFullRange = !this.structureDirty && candidates.length === this.entries.length;
      // 空间树只认领实际候选，临时高亮或导航器增删不能让无关模型索引反复重建。
      for (let id = 0; id < candidates.length; id++) {
        // 索引统一以 Object3D 身份查询，输出仍保留调用方的泛型候选类型。
        const object: Object3D = candidates[id];
        let entry = orderedFullRange ? this.entries[id] : this.entryByObject.get(object);
        // 必须逐项确认身份，不能仅凭数量相同就把局部/重复候选当成全范围。
        // 稳定全范围可直接读取密集记录，避免重复 Map 定位与去重代次写入。
        if (orderedFullRange && entry!.mesh !== object) {
          orderedFullRange = false;
          entry = this.entryByObject.get(object);
        }
        if (!entry && object instanceof Mesh && this.members.has(object)) {
          entry = { mesh: object, matrixWorld: new Matrix4(), worldBounds: new Box3(),
            geometryRevision: -1, bounded: false, hitQuery: -1, indexSlot: -1, boundsQuery: -1 };
          this.entryByObject.set(object, entry); this.entries.push(entry); this.structureDirty = true;
        }
        this.candidateEntries[id] = entry;
      }
      const useIndex = this.entries.length >= SceneMeshRaycastPolicy.minimumCandidates;
      if (useIndex) {
        // 树可以保留其他范围的旧盒：它们不在输出候选内；当前范围的每个盒必须先变成最新。
        // 更新当前叶及其祖先后，树仍保守包含本次全部候选，不需要扫描其他历史参与者。
        if (orderedFullRange) {
          for (let id = 0; id < this.entries.length; id++) {
            const entry = this.entries[id];
            if (this.refreshBounds(entry)) this.index.setBounds(id, entry.bounded ? entry.worldBounds : null);
          }
        } else {
          for (let id = 0; id < candidates.length; id++) {
            const entry = this.candidateEntries[id];
            if (entry) this.validateCandidateBounds(entry);
          }
        }
        if (this.structureDirty) {
          this.index.reset(this.entries.length);
          // 结构变化才重新登记全部槽，范围外条目沿用缓存；其重新进入候选时仍需版本校验。
          for (let id = 0; id < this.entries.length; id++) {
            const entry = this.entries[id];
            entry.indexSlot = id;
            this.index.setBounds(id, entry.bounded ? entry.worldBounds : null);
          }
        }
        this.structureDirty = false;
        this.index.query(ray, this.hits);
        for (const id of this.hits) this.entries[id].hitQuery = this.query;
      }
      let outputCount = 0;
      for (let id = 0; id < candidates.length; id++) {
        const entry = this.candidateEntries[id];
        // 脱离场景的语义代理、点线与实例组继续使用各自专用拾取。
        if (!useIndex || !entry || entry.hitQuery === this.query) target[outputCount++] = candidates[id];
      }
      target.length = outputCount;
      this.lastOutput = target.length;
    } finally { this.candidateEntries.length = 0; }
  }

  /** @returns 显式统计快照，不在射线热路径创建统计对象。 */
  public getStats(): SceneMeshRaycastStats {
    return { members: this.members.size, meshes: this.entries.length, geometryBoundsBuilds: this.geometryBoundsBuilds,
      refreshedBounds: this.refreshedBounds, lastInput: this.lastInput, lastOutput: this.lastOutput, index: this.index.getStats() };
  }

  /** 幂等释放监听、对象引用和 CPU 索引，不释放调用方几何或材质。 */
  public dispose(): void {
    this.unsubscribe?.(); this.unsubscribe = null;
    this.entryByObject.clear(); this.members.clear();
    this.entries.length = this.hits.length = this.candidateEntries.length = 0;
    this.geometryBounds = new WeakMap();
    this.index.dispose();
  }

  /** @param object 新成员；实例组由自己的槽位索引负责。 */
  private readonly onObjectAdded = (object: Object3D): void => {
    if (!(object instanceof Mesh) || ('isInstancedMesh' in object && object.isInstancedMesh)) return;
    this.members.add(object);
  };

  /** @param object 已离开成员，不等待下一次查询才释放引用。 */
  private readonly onObjectRemoved = (object: Object3D): void => {
    if (object instanceof Mesh) this.members.delete(object);
    const entry = this.entryByObject.get(object);
    if (!entry) return;
    this.entryByObject.delete(object);
    this.entries.splice(this.entries.indexOf(entry), 1);
    this.structureDirty = true;
  };

  /**
   * 当前输入中的同一对象只校验一次，重复项仍在最终输出中按原顺序保留。
   * @param entry 本次确实参与拾取的成员。
   */
  private validateCandidateBounds(entry: SceneMeshRaycastEntry): void {
    if (entry.boundsQuery === this.query) return;
    const changed = this.refreshBounds(entry);
    entry.boundsQuery = this.query;
    if (changed && !this.structureDirty) this.index.setBounds(entry.indexSlot, entry.bounded ? entry.worldBounds : null);
  }

  /** @param entry 当前记录。 @returns 世界边界或可靠性变化时为 true。 */
  private refreshBounds(entry: SceneMeshRaycastEntry): boolean {
    const mesh = entry.mesh;
    const geometry = mesh.geometry;
    const position = geometry.getAttribute('position');
    const matrix = mesh.matrixWorld.elements;
    // 蒙皮、morph、自定义 getVertexPosition/raycast 不能由静态位置缓冲推断边界。
    const supported = (mesh.raycast === Mesh.prototype.raycast || mesh.raycast === acceleratedRaycast)
      && mesh.getVertexPosition === Mesh.prototype.getVertexPosition
      && !geometry.morphAttributes.position?.length
      && position && !('isGLBufferAttribute' in position);
    if (!supported) {
      const changed = entry.bounded;
      entry.bounded = false;
      return changed;
    }
    const local = this.resolveGeometryBounds(geometry, position);
    if (entry.bounded && entry.geometryRevision === local.revision
      && entry.matrixWorld.equals(mesh.matrixWorld)) return false;
    // 稳定矩阵只做引用值比较；有限性和可逆性检查仅随实际矩阵/几何变化执行。
    if (matrix[3] !== 0 || matrix[7] !== 0 || matrix[11] !== 0 || matrix[15] !== 1
      || !matrix.every(Number.isFinite) || mesh.matrixWorld.determinant() === 0) {
      const changed = entry.bounded;
      entry.bounded = false;
      return changed;
    }
    entry.matrixWorld.copy(mesh.matrixWorld);
    entry.geometryRevision = local.revision; entry.bounded = true;
    entry.worldBounds.copy(local.bounds).applyMatrix4(mesh.matrixWorld);
    // 世界变换与精确求交的运算顺序不同，保留保守浮点边距，不改屏幕点线阈值。
    if (!entry.worldBounds.isEmpty()) {
      const bounds = entry.worldBounds;
      const scale = Math.max(1, Math.abs(bounds.min.x), Math.abs(bounds.min.y), Math.abs(bounds.min.z),
        Math.abs(bounds.max.x), Math.abs(bounds.max.y), Math.abs(bounds.max.z));
      bounds.expandByScalar(scale * Number.EPSILON * 32);
    }
    this.refreshedBounds++;
    return true;
  }

  /**
   * 读取共享位置缓冲的自有局部盒，不污染几何/BVH 所有者的缓存。
   * @param geometry 当前几何。
   * @param position 可由 CPU 读取的位置属性。
   * @returns 共享边界修订。
   */
  private resolveGeometryBounds(geometry: BufferGeometry, position: BufferAttribute | InterleavedBufferAttribute): SceneMeshGeometryBounds {
    const version = 'data' in position ? position.data.version : position.version;
    let cached = this.geometryBounds.get(geometry);
    if (cached && cached.position === position && cached.version === version && cached.count === position.count) return cached;
    if (!cached) {
      cached = { position, version, count: position.count, revision: 0, bounds: new Box3() };
      this.geometryBounds.set(geometry, cached);
    }
    cached.bounds.setFromBufferAttribute(position);
    cached.position = position; cached.version = version; cached.count = position.count;
    // 记录只保存唯一数字修订，范围外几何替换后不会被旧候选记录额外保活。
    cached.revision = ++this.nextGeometryRevision;
    this.geometryBoundsBuilds++;
    return cached;
  }
}
